import React from 'react';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';

interface Props {
  title: string;
  message: string;
  imageUrl?: string;
}

/**
 * Approximation of how the notification lands on an Android phone.
 *
 * Not pixel-perfect — the point is that the admin sees the *truncation*
 * before 30,000 people do. Android collapses the body to roughly two lines,
 * so a long message loses its ending, and that is exactly the mistake this
 * preview is here to catch.
 */
const PushPreview: React.FC<Props> = ({ title, message, imageUrl }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        پیش‌نمایش روی گوشی
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 1.25,
          borderRadius: 3,
          bgcolor: dark ? '#1c1c1e' : '#f2f2f7',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
          {/* The status-bar glyph: the brand glyph, tinted by the app colour. */}
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: '#B25373',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontSize: 11,
              fontWeight: 900,
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            G
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
            کازماشاپ
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            • هم‌اکنون
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={800}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title.trim() || 'عنوان اطلاعیه'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.7,
              }}
            >
              {message.trim() || 'متن اطلاعیه اینجا نمایش داده می‌شود…'}
            </Typography>
          </Box>

          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt=""
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                objectFit: 'cover',
                flexShrink: 0,
              }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                flexShrink: 0,
                bgcolor: '#1A2238',
                color: '#F5B82E',
                display: 'grid',
                placeItems: 'center',
                fontSize: 20,
                fontWeight: 900,
              }}
            >
              G
            </Box>
          )}
        </Stack>
      </Paper>

      {message.trim().length > 120 && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.75 }}>
          متن طولانی است — روی گوشی فقط حدود دو خط اول دیده می‌شود.
        </Typography>
      )}
    </Box>
  );
};

export default PushPreview;
